import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Drumstick, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    const res = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (res.ok) {
      navigate("/", { replace: true });
    } else {
      setErr(res.error || "Error al iniciar sesión");
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-900">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/4253296/pexels-photo-4253296.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-900/80 to-blue-900/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
              <Drumstick className="w-6 h-6" />
            </div>
            <div>
              <div className="text-lg font-bold tracking-tight">Cerebro Financiero</div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-blue-300 mb-3">
              Gestión Financiera Profesional
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
              Cada pollo cuenta.<br />
              <span className="text-blue-400">Cada peso importa.</span>
            </h1>
            <p className="mt-6 text-slate-300 max-w-md">
              Captura ventas y gastos en segundos. Conoce tu utilidad real, food cost y ganancia por pollo
              en tiempo real, desde cualquier dispositivo.
            </p>
          </div>
          <div className="text-xs text-slate-400">
            © 2026 · Sistema de gestión multisucursal
          </div>
        </div>
      </div>

      {/* Right login form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center text-white">
              <Drumstick className="w-6 h-6" />
            </div>
            <div>
              <div className="font-bold tracking-tight">Cerebro Financiero</div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">
              Iniciar sesión
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">Bienvenido</h2>
            <p className="text-sm text-slate-500 mt-1">Ingresa tus credenciales para continuar.</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Usuario / Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                data-testid="login-email"
                className="mt-1.5 bg-blue-50 border-blue-200 focus-visible:ring-blue-500 focus-visible:border-blue-500 text-blue-900"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Contraseña
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                  className="bg-blue-50 border-blue-200 focus-visible:ring-blue-500 focus-visible:border-blue-500 text-blue-900 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {err && (
            <div
              data-testid="login-error"
              className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
          >
            {busy ? "Ingresando…" : "Ingresar"}
          </Button>

          <div className="text-xs text-slate-400 text-center">
            Acceso protegido · Cifrado de extremo a extremo
          </div>
        </form>
      </div>
    </div>
  );
}
